import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useForm } from "react-hook-form";
import {
  loginPasswordValidator,
  validateAge,
  loginUsernameValidator,
  passwordRules,
} from "../../Validator/register";
import PasswordChecklist from "react-password-checklist";
import { ApiHook } from "../../hooks/apiHook";
import { BASE_URL } from "../../config/config";
import DatePickerComponent from "../Common/DatePickerComponent";
import dayjs from "dayjs";
import { PhoneInput } from "react-international-phone";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useSelector } from "react-redux";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import MyPayPalOrderButton from "../payment/PaypalOrderButton";
import Skeleton from "react-loading-skeleton";
import Visibility from "@mui/icons-material/Visibility";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import Stripe from "../../views/payment/Stripe";
import { toast } from "react-toastify";
import { ReplicaService } from "../../services/replica/replica";
import { debounce } from "@mui/material";

const ReplicaRegisterForm = ({
  activeStep,
  setActiveStep,
  handleToggleRegisterModal,
  animateStepTransition,
  data,
  isLogin,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("");
  const [states, setStates] = useState([]);
  const [submitButtonActive, setSubmitButtonActive] = useState(false);
  const [file, setFile] = useState(null);
  const [selectedDob, setSelectedDob] = useState(dayjs());
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedGender, setSelectedGender] = useState();
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [stripePublicKey, setStripePublicKey] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [stripeIntentStatus, setStripeIntentStatus] = useState("");
  const [isOtpSend, setIsOtpSend] = useState(false);

  const [sponsorsOptions, setSponsorsOptions] = useState([]);
  const [isLoadingSponsors, setIsLoadingSponsors] = useState(false);
  const [sponsorSearchValue, setSponsorSearchValue] = useState("");
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  const dateOfBirthField = data?.contactInformation?.contactField?.find(
    (item) => item.code === "dateOfBirth"
  );
  const phoneFiled = data?.contactInformation?.contactField?.find(
    (item) => item.code === "mobile"
  );
  const ageLimit = dateOfBirthField?.options[0]?.validation.ageLimit;

  const [isMinAge, setIsMinAge] = useState({
    status: false,
    ageLimit: ageLimit,
  });
  const [isMaxAge, setIsMaxAge] = useState({
    status: false,
    ageLimit: 120,
  });
  const [fileResponse, setFileResponse] = useState({
    success: null,
    error: null,
  });
  const regAmount = useSelector(
    (state) => state?.dashboard?.appLayout?.configuration?.regAmount
  );
  const conversionFactor = useSelector(
    (state) => state?.user?.conversionFactor
  );
  const userSelectedCurrency = useSelector(
    (state) => state.user?.selectedCurrency
  );
  const sponsorImage = useSelector(
    (state) => state.replica?.sponsor?.userDetails?.image
  );

  const {
    register,
    setValue,
    trigger,
    watch,
    setError,
    getValues,
    formState: { errors },
  } = useForm();
  const phoneUtil = PhoneNumberUtil.getInstance();
  const formValues = watch();
  // console.log(formValues);
  // console.log(errors,"error");

  const steps = data?.registrationSteps;

  // ------------------------------------------- API ------------------------------------------
  const deleteBankRecipt = ApiHook.CallReplicaBankRecieptDelete(
    setSubmitButtonActive,
    setValue,
    setFileResponse,
    setFile
  );
  const checkUsernameField = ApiHook.CallReplicaFieldCheck();
  const checkEmailField = ApiHook.CallReplicaFieldCheck();
  const Upload = ApiHook.CallReplicaBankRecieptUpload(
    "register/replica",
    formValues?.username,
    data?.sponsorData?.id,
    setSubmitButtonActive,
    setValue,
    setFileResponse
  );
  const replicaRegisterMutation = ApiHook.CallReplicaRegisterPost();
  const stripeIntent = ApiHook.CallReplicaCreateStripeIntent();
  const updateStripeIntent = ApiHook.CallReplicaUpdateStripeIntent();
  const cancelStripeIntent = ApiHook.CallReplicaCancelStripeIntent();
  const sendEmailVerification = ReplicaService.useSendVerificationCode();
  const verifyEmail = ReplicaService.useVerifyEmail();
  const searchSponsors = ReplicaService.useSponsorSearch();

  // ---------------------------------- Functions ------------------------------------

  const debouncedSearch = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm && searchTerm.length >= 2) {
        setIsLoadingSponsors(true);
        try {
          const response = await searchSponsors.mutateAsync({
            query: searchTerm,
          });
          if (response?.data) {
            // Transform the API response to match react-select format
            const formattedOptions = response.data.map((sponsor) => ({
              value: sponsor.id,
              label: `${sponsor.username}`,
              // label: `${sponsor.username} - ${
              //   sponsor.fullName || sponsor.name
              // }`,
              data: sponsor, // Store full sponsor data
            }));
            setSponsorsOptions(formattedOptions);
          }
        } catch (error) {
          console.error("Error searching sponsors:", error);
          setSponsorsOptions([]);
        }
        setIsLoadingSponsors(false);
      } else {
        setSponsorsOptions([]);
      }
    }, 500), // 500ms debounce
    []
  );

  // Handle input change for sponsor search
  const handleSponsorInputChange = (inputValue) => {
    setSponsorSearchValue(inputValue);
    debouncedSearch(inputValue);
  };

  // Handle sponsor selection
  const handleSponsorChange = (selectedOption) => {
    setValue("referralId", selectedOption?.value);
    setSelectedSponsor(selectedOption ?? null);
  };

  const maxAgeValidation = (selectedDate, ageLimit) => {
    if (selectedDate) {
      const today = dayjs();
      const maxAge = today.subtract(ageLimit, "year"); // Calculate the max Age
      return !selectedDate.isAfter(maxAge);
    } else {
      return false;
    }
  };
  const genderOptions = (data) => {
    return data.map((item) => ({
      value: item.value,
      label: t(item.title),
    }));
  };
  const countryList = (data) => {
    return data.map((item) => ({
      label: item?.name,
      value: item?.id,
    }));
  };
  const stateList = (data) => {
    if (!formValues?.country) {
      return []; // Return an empty array if there's no country specified.
    }

    const selectedCountry = data.find(
      (country) => country.id === Number(formValues.country)
    );

    if (!selectedCountry) {
      return []; // Return an empty array if the selected country is not found.
    }

    return selectedCountry.States.map((state) => ({
      value: state.id,
      label: state.name,
    }));
  };

  const districtList = () => {
    if (!formValues?.state) {
      return []; // Return an empty array if there's no state specified.
    }

    const selectedState = data?.districtsData?.filter(
      (state) => state.stateId === Number(formValues.state)
    );

    if (!selectedState) {
      return []; // Return an empty array if the selected state is not found.
    }

    return selectedState?.map((district) => ({
      value: district.value,
      label: district.label,
    }));
  };

  const changeCountry = (country) => {
    setSelectedCountry(country?.value);
    setSelectedState(null); // Clear the selected state
    setValue("state", null);
    setValue("country", country?.value); // Update the form value for country
  };

  useEffect(() => {
    if (data?.contactInformation) {
      const defCountry = data?.contactInformation?.contactField?.find(
        (item) => item.code === "country"
      )?.options[0]?.id;
      setSelectedCountry(defCountry);
      setValue("country", defCountry);
    }
  }, [data?.contactInformation]);

  const handleDateChange = (newDate, item) => {
    if (newDate) {
      setIsMinAge({
        status: minAgeValidation(
          newDate,
          item.options[0]?.validation?.ageLimit
        ),
        ageLimit: item.options[0]?.validation?.ageLimit,
      });
      setIsMaxAge({
        status: maxAgeValidation(newDate, 120),
        ageLimit: 120,
      });
      setSelectedDob(newDate);
      const formattedDate = newDate.format("YYYY-MM-DD");
      setValue("dateOfBirth", formattedDate);
      setError("dateOfBirth", { message: "" });
    }
  };
  const handleDeleteBankReciept = () => {
    const data = {
      filepath: formValues?.bankReceipt,
      type: "replica",
    };
    if (formValues?.bankReceipt) {
      deleteBankRecipt.mutateAsync(data);
    }
  };
  const genderChange = (gender) => {
    setSelectedGender(gender);
    setValue("gender", gender?.value);
  };
  const changeState = (state) => {
    setSelectedState(state?.value);
    setValue("state", state?.value);
  };
  const changeDistrict = (state) => {
    setSelectedDistrict(state?.value);
    setValue("city", state?.value);
  };
  const minAgeValidation = (selectedDate, ageLimit) => {
    if (selectedDate) {
      const today = dayjs();
      const minAge = today.subtract(ageLimit, "year"); // Calculate the minimum Age
      return selectedDate.isBefore(minAge);
    } else {
      // Handle the case when selectedDate is null or undefined
      return false; // Or you can throw an error or handle it differently
    }
  };
  const handleNext = async () => {
    let isValid;
    if (
      activeStep === 3 &&
      !checkUsernameField.data?.data?.status &&
      checkUsernameField.data?.data?.field === "username" &&
      checkUsernameField.data?.data?.code === 1117
    ) {
      isValid = false;
    } else if (
      activeStep === 2 &&
      !checkEmailField.data?.data?.status &&
      checkEmailField.data?.data?.field === "email" &&
      checkEmailField.data?.data?.code === 1117
    ) {
      isValid = false;
    } else if (activeStep === 1 && data?.regData) {
      setValue("pv", data?.regData);
      isValid = true;
    } else {
      isValid = await trigger();
    }
    if (phoneFiled && !formValues?.mobile && activeStep === 2) {
      isValid = false;
      setError("mobile", { message: t("this_field_is_required") });
    }
    // if(!verifyEmail.data?.status) {
    //   isValid = false;
    //   setError("email", { message: "email is not Verified" });
    // }
    if (formValues?.password?.includes(" ") && activeStep === 3) {
      isValid = false;
      setError("password", { message: "password_cannot_contain_white_spaces" });
    }
    if (phoneFiled && !isPhoneNumberValid && activeStep === 2) {
      isValid = false;
      setError("mobile", {
        message: t("min_length"),
      });
    }
    // age validation
    if (dateOfBirthField && !formValues?.dateOfBirth && activeStep === 2) {
      isValid = setError("dateOfBirth", {
        message: t("this_field_is_required"),
      });
    }
    if (!isMinAge?.status && isMinAge?.ageLimit && activeStep === 2) {
      isValid = setError("dateOfBirth", {
        message: t("ageValidation", { age: isMinAge?.ageLimit }),
      });
    } else if (isMaxAge?.status && isMaxAge?.ageLimit && activeStep === 2) {
      isValid = setError("dateOfBirth", {
        message: t("maxAgeLimit", { age: isMaxAge?.ageLimit }),
      });
    }
    //name validation
    // if (activeStep === 2 && formValues?.firstName.length > 15) {
    //   isValid = false;
    //   setError("firstName", {
    //     message: t("max_length_is"),
    //   });
    // }

    // if (activeStep === 2 && formValues?.lastName?.length > 15) {
    //   isValid = false;
    //   setError("lastName", {
    //     message: t("max_length_is"),
    //   });
    // }
    // auto select if there is only single payment method
    if (data?.paymentGateways.length === 1) {
      const defaultPayment = data?.paymentGateways[0];
      setActiveTab(defaultPayment.id);
      if (defaultPayment.id === 3) {
        setSubmitButtonActive(false);
      } else {
        setSubmitButtonActive(true);
      }
      setValue("paymentType", String(defaultPayment.id));
    }
    // below is international mobile validation : uncomment if needed

    // if (!isPhoneNumberValid && activeStep === 2) {
    //   isValid = false;
    //   setError("mobile",{message:t("invalidPhone")});
    // }

    if (isValid) {
      const nextStep = activeStep + 1;
      animateStepTransition(nextStep);
      setActiveStep(nextStep);
    }
  };

  const handleBack = () => {
    if (paymentIntentId !== "") {
      const body = {
        stripeStatus: stripeIntentStatus,
        paymentIntentId: paymentIntentId,
      };
      cancelStripeIntent.mutateAsync(body, {
        onSuccess: (res) => {
          if (res.status === "canceled") {
            // clear stripe
            setPaymentIntentId("");
            setClientSecret("");
            setStripeIntentStatus("");
          }
        },
      });
    }
    setActiveTab("");
    const prevStep = activeStep - 1;
    animateStepTransition(prevStep);
    setActiveStep(prevStep);
    // below step is to clear the uploaded image & value
    if (activeStep === 4 || activeStep === 5) {
      setValue("bankReceipt", undefined);
    }
    if (file) {
      setFile(null);
    }
  };

  const handleSubmit = (
    paymentId,
    paymentIntent,
    paymentType,
    paypalResponse
  ) => {
    if (paymentType === "stripe") {
      formValues.stripeResponse = paymentIntent;
    }
    if (paymentType === "paypal") {
      formValues.paymentType = paymentId;
      formValues.paypalResponse = paypalResponse;
    }

    if (data?.sponsorData?.username && !isLogin) {
      setValue("placement", data?.sponsorData?.username);
      formValues.placement = data?.sponsorData?.username;
      setValue("referralId", data?.sponsorData?.id);
      formValues.referralId = data?.sponsorData?.id;
    }else {
      setValue("placement", data?.sponsorData?.username);
      formValues.placement = data?.sponsorData?.username;
    }
    formValues.paymentType = "4";
    formValues.position = formValues.position ?? data?.position;

    const payload = removeCustomFields(
      formValues,
      data?.contactInformation?.customField
    );
    replicaRegisterMutation.mutate(payload);
  };

  const handleProductClick = (productId, productName, pv, price) => {
    const totalAmount = Number(price) + JSON.parse(data?.regFee);
    setValue("totalAmt", `${CurrencyConverter(totalAmount, conversionFactor)}`);
    setValue(
      "product",
      { id: productId, name: productName, price: price },
      { shouldValidate: true }
    );
    setValue("pv", pv);
    setValue("totalAmount", totalAmount.toFixed(2));
    setValue("regAmount", data?.regFee);
  };

  const handlePaymentTabClick = (tabId, title) => {
    setActiveTab(tabId);
    if (formValues?.bankReceipt !== undefined) {
      setValue("bankReceipt", undefined);
    }
    if (tabId === 3) {
      setSubmitButtonActive(false);
    } else if (title === "stripe") {
      setSubmitButtonActive(true);
      setStripePublicKey(
        data.paymentGateways.find((item) => item.title === "stripe").publicKey
      );
      if (paymentIntentId === "") {
        const body = {
          productId: formValues.product.id,
        };
        stripeIntent.mutateAsync(body, {
          onSuccess: (res) => {
            if (res.status) {
              setClientSecret(res.client_secret);
              setPaymentIntentId(res.id);
              setStripeIntentStatus(res.status);
            } else {
              toast.error(res.description);
            }
          },
        });
      } else {
        const body = {
          productId: formValues.product.id,
          paymentIntentId: paymentIntentId,
        };
        updateStripeIntent.mutateAsync(body, {
          onSuccess: (res) => {
            setStripeIntentStatus(res.status);
          },
        });
      }
    } else if (title === "bank-transfer") {
      if (formValues?.bankReceipt === undefined) {
        setSubmitButtonActive(true);
      }
    } else {
      setSubmitButtonActive(true);
    }
    setValue("paymentType", tabId, { shouldValidate: true }); // Set the selected payment
    if (!data?.modStatus?.productStatus) {
      setValue("totalAmount", data?.regData?.toFixed(2)); // regAmount is added to total amount. If there is no product
    }
  };

  const handleEmailCheck = async (item) => {
    const { name, value } = item;
    checkEmailField.mutate({ field: name, value: value });
  };

  const handleUsernameCheck = async (item) => {
    const { name, value } = item;
    checkUsernameField.mutate({ field: name, value: value });
  };

  const handleCustomField = (id, value) => {
    let custom = { ...formValues.customFields };
    custom[id] = value;
    setValue("customFields", custom);
  };

  const handleFileChange = (event) => {
    setFileResponse({
      success: null,
      error: null,
    });
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
  };

  const handleUpload = () => {
    if (file) {
      Upload.mutate(file);
    }
  };

  const handleCountry = (selectedCountry, statesData) => {
    if (selectedCountry) {
      setValue("country", selectedCountry);
      formValues.country = selectedCountry;
    }
    statesData?.map((value) => {
      if (parseInt(selectedCountry) === value.id) {
        setStates(value.States);
      }
    });
  };
  const handlePhoneNumber = (phone) => {
    setPhoneNumber(phone);
    setValue("mobile", phone);
    setError("mobile", { message: "" });
  };
  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const isPhoneValid = (phone) => {
    // international phoneUtil validation is commented
    // return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));

    // Add minimum length validation
    return phone.length >= 7 && !isNaN(phone);
  };
  const isPhoneNumberValid = isPhoneValid(phoneNumber);

  const removeCustomFields = (payload, customField) => {
    customField.forEach((field) => {
      const key = field.code;
      delete payload[key];
    });
    return payload;
  };

  useEffect(() => {
    if (data?.modStatus?.productStatus === 0) {
      if (data?.regFee === 0) {
        setValue("pv", 0);
      } else {
        setValue("pv", data?.regFee);
      }
    }
  }, []);

  const sendVerificationCode = () => {
    sendEmailVerification.mutate(formValues.email, {
      onSuccess: (res) => {
        if (res.status) {
          toast.success("Verification code sent successfully");
          setIsOtpSend(true);
        } else {
          toast.error(res.description);
        }
      },
      onError: (error) => {
        console.error("Failed to send verification code", error);
      },
    });
  };

  const handleVerificationCodeChange = (event) => {
    // setVerificationCode(event.target.value);
    if (event.target.value.length === 6) {
      verifyEmail.mutate(
        { email: formValues.email, otp: event.target.value },
        {
          onSuccess: (res) => {
            if (res.status) {
              toast.success("Email verified successfully");
              setIsOtpSend(false);
            } else {
              toast.error(res.description);
            }
          },
          onError: (error) => {
            console.error("Failed to verify email", error);
          },
        }
      );
    }
  };

  return (
    <div className="main-content-regsiter" style={{ margin: "65px" }}>
      <div className="row justify-content-center pt-0 p-4" id="wizardRow">
        <div className="col-md-12 text-center">
          <div className="wizard-form py-4 my-2">
            <ul id="progressBar" className="progressbar px-lg-5 px-0">
              {steps.map((step, index) => (
                <li
                  key={`step-${index + 1}`}
                  id={`progressList-${index + 1}`}
                  className={`d-inline-block w-20 position-relative text-center float-start progressbar-list ${
                    data?.regPack === null && data?.regFee === 0
                      ? activeStep - 1 >= step.step
                        ? "active"
                        : ""
                      : activeStep >= step.step
                      ? "active"
                      : ""
                  }`}
                >
                  {t("step")}
                  {step.step}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div id="animation">
        {activeStep === 1 && (data?.regFee !== 0 || data?.regPack !== null) && (
          <div className="row row_top justify-content-center" id="cardSection">
            <div className="col-lg-12 col-md-12">
              <div className="regsiter_step_1_view">
                <div className="row">
                  <div className="col-md-12">
                    <div className="right_product_selection_bx">
                      <div className="regsiter_step_1_view_left_sec_head">
                        {data?.registrationSteps &&
                          t(
                            `${data?.registrationSteps[activeStep - 1]?.label}`
                          )}
                      </div>
                      <div className="right_product_lsting_section">
                        <div className="row">
                          {(data?.regData || data?.regData === 0) && (
                            <div className="col-md-4">
                              <div
                                className={`right_product_box card active-card`}
                                {...register("product")}
                              >
                                <div className="right_product_box_image">
                                  <img src={"/images/product1.jpg"} alt="" />
                                </div>
                                <div className="right_product_box__dtails">
                                  <div className="right_product_box__head">
                                    {/* {product?.name} */}
                                  </div>
                                  <div className="right_product_box__dsc">
                                    <strong id="price">{`${
                                      userSelectedCurrency?.symbolLeft
                                    } ${CurrencyConverter(
                                      data?.regData,
                                      conversionFactor
                                    )}`}</strong>
                                    <br />
                                    {"PV - "}
                                    {data?.regData}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn text-white float-end next mt-4 rounded-3 bg-color-info"
                                  onClick={() => handleNext()}
                                >
                                  {t("buy_now")}
                                </button>
                              </div>
                            </div>
                          )}
                          {data?.regPack?.map((product, index) => (
                            <div className="col-md-4" key={index}>
                              <div
                                className={`right_product_box card ${
                                  formValues.product?.id === product.id
                                    ? "active-card"
                                    : ""
                                }`}
                                {...register("product")}
                                onClick={() =>
                                  handleProductClick(
                                    product.id,
                                    product.name,
                                    product.pairValue,
                                    product.price
                                  )
                                }
                              >
                                <div className="right_product_box_image">
                                  <img
                                    src={
                                      product?.image ?? "/images/product1.jpg"
                                    }
                                    alt=""
                                  />
                                </div>
                                <div className="right_product_box__dtails">
                                  <div className="right_product_box__head">
                                    {product?.name}
                                  </div>
                                  <div className="right_product_box__dsc">
                                    <strong id="price">{`${
                                      userSelectedCurrency?.symbolLeft
                                    } ${CurrencyConverter(
                                      product?.price,
                                      conversionFactor
                                    )}`}</strong>
                                    <br />
                                    {"PV - "}
                                    {product?.pairValue}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn text-white float-end next mt-4 rounded-3 bg-color-info"
                                  onClick={() => handleNext()}
                                >
                                  {t("buy_now")}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeStep === 2 && (
          <div className="row row_top justify-content-center form-business">
            <div style={{ display: "flex", justifyContent: "start" }}>
              {!isLogin && (
                <div className="col-lg-7">
                  <div className="regsiter_step_1_view_left_sec_head">
                    {t("sponsor")}
                  </div>
                  <div className="regsiter_step_1_view_left_user_bx_1">
                    <div className="regsiter_step_1_view_left_user_bx_image_1">
                      <img
                        src={sponsorImage ?? "/images/user-profile.png"}
                        alt=""
                      />
                    </div>
                    <div className="regsiter_step_1_view_left_user_bx_txt">
                      <strong>{data?.sponsorData?.username}</strong>
                      <p>
                        {data?.sponsorData?.UserDetail?.fullName ? (
                          <>
                            {data?.sponsorData?.UserDetail?.fullName}
                            <br />
                            {data?.sponsorData?.email}
                          </>
                        ) : (
                          <Skeleton count={2} />
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="col-lg-5">
                {/* {!data?.position && data?.modStatus?.mlmPlan === "Binary" && ( */}
                {data?.position !== "L" && data?.position !== "R" && (
                  <>
                    <div className="regsiter_step_1_view_left_sec_head">
                      {t("placement")}
                    </div>
                    <div className="regsiter_second_step_bx reg_select_pos">
                      <div className="regsiter_step_1_view_left_btn_row mt-0">
                        <label
                          // className={`regsiter_step_1_view_left_btn ${formValues?.position === "L" ? "active" : ""
                          //   }`}
                          className={`regsiter_step_1_view_left_btn ${
                            (formValues?.position || data?.position) === "L"
                              ? "active"
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="position"
                            value={"L"}
                            {...register("position", {
                              required: {
                                value:
                                  (data?.position !== "L" &&
                                    data?.position !== "R") ||
                                  !data?.position,
                                message: t("this_field_is_required"),
                              },
                            })}
                          />
                          {t("left")}
                        </label>
                        <label
                          // className={`regsiter_step_1_view_left_btn ${formValues?.position === "R" ? "active" : ""
                          //   }`}
                          className={`regsiter_step_1_view_left_btn ${
                            (formValues?.position || data?.position) === "R"
                              ? "active"
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="position"
                            value={"R"}
                            {...register("position", {
                              required: {
                                value:
                                  (data?.position !== "L" &&
                                    data?.position !== "R") ||
                                  !data?.position,
                                message: t("this_field_is_required"),
                              },
                            })}
                          />
                          {t("right")}
                        </label>
                      </div>
                      {errors["position"] && formValues.position === null && (
                        <span className="error-message-validator">
                          {t("this_field_is_required")}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="register_step_1_view_left_sec_head">
              {data?.registrationSteps &&
                (data.regPack === null && data.regFee === 0
                  ? t(data?.registrationSteps[activeStep - 2]?.label)
                  : data.regPack === null && data.regFee !== 0
                  ? t(data?.registrationSteps[activeStep - 1]?.label)
                  : t(data?.registrationSteps[activeStep - 1]?.label))}
            </div>

            <div className="regsiter_second_step_bx">
              <div className="row">
                {isLogin && (
                  <div className="select-sponsor">
                    <div className="regsiter_form_box">
                      <label htmlFor={"referralId"}>
                        Sponsor
                        <span className="text-danger">٭</span>
                      </label>
                      <Select
                        id="referralId"
                        name="referralId"
                        className={`dropdown-common ${
                          errors["referralId"] ? "error-field" : ""
                        }`}
                        {...register("referralId", {
                          required: {
                            value: true,
                            message: t("this_field_is_required"),
                          },
                        })}
                        onBlur={async () => await trigger("referralId")}
                        options={sponsorsOptions}
                        onInputChange={handleSponsorInputChange}
                        onChange={handleSponsorChange}
                        value={selectedSponsor}
                        inputValue={sponsorSearchValue}
                        isLoading={isLoadingSponsors}
                        isSearchable={true}
                        isClearable={true}
                        placeholder={"Search sponsor by username"}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue.length < 2
                            ? "Type at least 2 characters"
                            : isLoadingSponsors
                            ? "Searching..."
                            : "No sponsors found"
                        }
                        classNamePrefix="react-select"
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: "38px",
                            height: "38px",
                            borderColor: state.isFocused ? "#2684FF" : "#ccc",
                            boxShadow: "none",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: "38px",
                            padding: "0 8px",
                            display: "flex",
                            alignItems: "center",
                          }),
                          input: (base) => ({
                            ...base,
                            margin: 0,
                            padding: 0,
                            alignSelf: "center",
                            display: "flex",
                            alignItems: "center",
                            lineHeight: "normal",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            margin: 0,
                            lineHeight: "38px",
                          }),
                          placeholder: (base) => ({
                            ...base,
                            margin: 0,
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%)",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: "38px",
                          }),
                        }}
                      />
                      {errors["referralId"] && (
                        <span className="error-message-validator">
                          {errors["referralId"].message}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {data?.contactInformation?.contactField?.map((item, index) => (
                  <div className="col-md-6" key={index}>
                    <div className="regsiter_form_box">
                      <label htmlFor={item.code}>
                        {t(`${item.code}`)}
                        <span className="text-danger" hidden={!item.required}>
                          ٭
                        </span>
                      </label>
                      {item.type === "text" &&
                        item.code !== "state" &&
                        item.code !== "city" &&
                        !item.options && (
                          <input
                            id={item.code}
                            name={item.code}
                            type="text"
                            className={`form-control ${
                              errors[item.code] ? "error-field" : ""
                            }`}
                            placeholder={item.placeholder}
                            {...register(item.code, {
                              required: {
                                value: item.required,
                                message: t("this_field_is_required"),
                              },
                              // pattern: {
                              //   value: /^[A-Za-z0-9]+$/,
                              //   message: t("invalid_format"),
                              // },
                            })}
                            onBlur={async () => await trigger(item.code)}
                          />
                        )}
                      {/* {item.type === "email" && (
                        <>
                          <input
                            id={item.code}
                            name={item.code}
                            disabled={verifyEmail.data?.status}
                            type="email"
                            className={`form-control ${
                              (!checkEmailField.data?.data?.status &&
                                checkEmailField.data?.data?.field === "email" &&
                                checkEmailField.data?.data?.code === 1117) ||
                              errors[item.code]
                                ? "error-field"
                                : ""
                            }`}
                            placeholder={item.placeholder}
                            {...register("email", {
                              required: {
                                value: item.required,
                                message: t("this_field_is_required"),
                              },
                              pattern: {
                                value:
                                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]+$/,
                                message: t("invalid_email_format"),
                              },
                            })}
                            onChangeCapture={async (e) => {
                              handleEmailCheck(e.target);
                            }}
                            onBlur={async () => await trigger(item.code)}
                          />
                          <div className="d-flex mt-2">
                            <input type="text" className="form-control" placeholder="Enter Verification Code" style={{ width: "250px" }} disabled={!isOtpSend} onChange={handleVerificationCodeChange} />
                            <button
                              className={`ms-2 btn ${verifyEmail.data?.status ? "btn-success bg-success": "btn-primary"  } px-2 py-0`}
                              onClick={sendVerificationCode}
                              // style={verifyEmail.data?.status ? {pointerEvents: "none"} : {}}
                              // disabled={formValues.email === "" || sendEmailVerification.isLoading || verifyEmail.data?.status}
                            disabled={formValues.email === "" ||  !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]+$/.test(formValues.email) || sendEmailVerification.isLoading || verifyEmail.data?.status || !checkEmailField.data?.data?.status &&
                            checkEmailField.data?.data?.field === "email" &&
                            checkEmailField.data?.data?.code === 1117}
                            >
                              {sendEmailVerification.isLoading ? (
                                <i class="fa-solid fa-circle-notch fa-spin px-3"></i>
                              ) : isOtpSend ? (
                                <><i class="fa-solid fa-repeat"></i> Resend</>
                              ) : verifyEmail.data?.status ? (
                                <><i class="fa-solid fa-circle-check"></i> Verified</>
                              ) : (
                                <><i class="fa-solid fa-paper-plane"></i> Verify</>
                              )}
                            </button>
                          </div>
                          {sendEmailVerification.data?.status && !verifyEmail.data?.status && <div className="text-success">verification code sent</div>}
                          {!checkEmailField.data?.data?.status &&
                            checkEmailField.data?.data?.field === "email" &&
                            checkEmailField.data?.data?.code === 1117 && (
                              <span className="error-message-validator">
                                {t("email_exists")}
                              </span>
                            )}
                        </>
                      )} */}
                      {item.type === "email" && (
                        <>
                          <input
                            id={item.code}
                            name={item.code}
                            type="email"
                            className={`form-control ${
                              (!checkEmailField.data?.data?.status &&
                                checkEmailField.data?.data?.field === "email" &&
                                checkEmailField.data?.data?.code === 1117) ||
                              errors[item.code]
                                ? "error-field"
                                : ""
                            }`}
                            placeholder={item.placeholder}
                            {...register(item.code, {
                              required: {
                                value: item.required,
                                message: t("this_field_is_required"),
                              },
                              pattern: {
                                value:
                                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]+$/,
                                message: t("invalid_email_format"),
                              },
                            })}
                            onBlur={async () => await trigger(item.code)}
                            onChangeCapture={(e) => {
                              handleEmailCheck(e.target);
                            }}
                          />
                          {!checkEmailField.data?.data?.status &&
                            checkEmailField.data?.data?.field === "email" &&
                            checkEmailField.data?.data?.code === 1117 && (
                              <span className="error-message-validator">
                                {t("email_exists")}
                              </span>
                            )}
                        </>
                      )}
                      {item.type === "date" && (
                        <DatePickerComponent
                          className={`date-picker ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          date={selectedDob}
                          handleChange={(newDate) =>
                            handleDateChange(newDate, item)
                          }
                        />
                      )}
                      {item.type === "number" && item.code === "mobile" && (
                        <PhoneInput
                          defaultCountry="in"
                          value={phoneNumber}
                          onChange={handlePhoneNumber}
                        />
                      )}
                      {item.type === "number" && !(item.code === "mobile") && (
                        <input
                          id={item.code}
                          name={item.code}
                          type="number"
                          className={`form-control ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          placeholder={item.placeholder}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                            minLength: {
                              value: 5,
                              message: t("min_length_of_5"),
                            },
                          })}
                          onBlur={async () => await trigger(item.code)}
                        />
                      )}
                      {item.type === "text" && item.code === "gender" && (
                        <Select
                          id={item.code}
                          name={item?.code}
                          className={`dropdown-common ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                          })}
                          onBlur={async () => await trigger(item.code)}
                          options={genderOptions(item?.options)}
                          onChange={genderChange}
                          value={selectedGender}
                          isSearchable={false}
                        />
                      )}
                      {item.type === "text" && item.code === "country" && (
                        <Select
                          isSearchable={true}
                          id={item.code}
                          name={item?.code}
                          className={`replica-reg-country ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                          })}
                          onBlur={async () => await trigger(item.code)}
                          value={countryList(item?.options).find(
                            (item) => item.value === selectedCountry
                          )}
                          options={countryList(item?.options)}
                          onChange={changeCountry}
                        />
                      )}
                      {item.type === "text" && item.code === "state" && (
                        <Select
                          // isSearchable={false}
                          id={item.code}
                          name={item?.code}
                          className={`replica-reg-country ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                          })}
                          options={stateList(item?.options)}
                          onBlur={async () => await trigger(item.code)}
                          value={stateList(item?.options).find(
                            (state) => state?.value === selectedState
                          )}
                          onChange={changeState}
                        />
                      )}
                      {item.type === "text" && item.code === "city" && (
                        <Select
                          // isSearchable={false}
                          id={item.code}
                          name={item?.code}
                          className={`replica-reg-country ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                          })}
                          options={districtList(item?.options)}
                          onBlur={async () => await trigger(item.code)}
                          value={districtList(item?.options).find(
                            (state) => state?.value === selectedDistrict
                          )}
                          onChange={changeDistrict}
                        />
                      )}
                      {errors[item.code] && (
                        <span className="error-message-validator">
                          {errors[item.code].message}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {data?.contactInformation?.customField?.map((item, index) => (
                  <div className="col-md-6" key={index}>
                    <div className="regsiter_form_box">
                      <label htmlFor={item.code}>
                        {item.value}{" "}
                        <span className="text-danger" hidden={!item.required}>
                          ٭
                        </span>
                      </label>
                      {item.type === "text" && !item.options && (
                        <input
                          id={item.code}
                          name={item.code}
                          type="text"
                          className={`form-control ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          placeholder={item.placeholder}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                            // pattern: {
                            //   value: /^[A-Za-z0-9]+$/,
                            //   message: t("invalid_format"),
                            // },
                          })}
                          onChange={async (e) => {
                            const inputValue = e.target.value; // Get the current value of the input field
                            handleCustomField(item.id, inputValue);
                            await trigger(item.code);
                          }}
                        />
                      )}
                      {item.type === "email" && (
                        <>
                          <input
                            id={item.code}
                            name={item.code}
                            type="email"
                            className={`form-control ${
                              (!checkEmailField.data?.data?.status &&
                                checkEmailField.data?.data?.field === "email" &&
                                checkEmailField.data?.data?.code === 1117) ||
                              errors[item.code]
                                ? "error-field"
                                : ""
                            }`}
                            placeholder={item.placeholder}
                            {...register(item.code, {
                              required: {
                                value: item.required,
                                message: t("this_field_is_required"),
                              },
                              pattern: {
                                value:
                                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]+$/,
                                message: t("invalid_email_format"),
                              },
                            })}
                            onChangeCapture={async (e) => {
                              const inputValue = e.target.value; // Get the current value of the input field
                              handleCustomField(item.id, inputValue);
                            }}
                          />
                          {!checkEmailField.data?.data?.status &&
                            checkEmailField.data?.data?.field === "email" &&
                            checkEmailField.data?.data?.code === 1117 && (
                              <span className="error-message-validator">
                                {t("email_exists")}
                              </span>
                            )}
                        </>
                      )}
                      {item.type === "date" && (
                        <>
                          <input
                            id={item.code}
                            name={item.code}
                            type="date"
                            className={`form-control ${
                              errors[item.code] ? "error-field" : ""
                            }`}
                            placeholder={item.placeholder}
                            {...register(item.code, {
                              required: {
                                value: item.required,
                                message: t("this_field_is_required"),
                              },
                              validate: (value) => validateAge(value, t),
                            })}
                            onChange={async (e) => {
                              const inputValue = e.target.value; // Get the current value of the input field
                              handleCustomField(item.id, inputValue);
                              await trigger(item.code);
                            }}
                          />
                          {errors.dateOfBirth && (
                            <span className="error-message-validator">
                              {errors.dateOfBirth.message}
                            </span>
                          )}
                        </>
                      )}
                      {item.type === "textarea" && (
                        <textarea
                          id={item.code}
                          name={item.code}
                          className={`form-control ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          placeholder={item.placeholder}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                          })}
                          onChange={async (e) => {
                            const inputValue = e.target.value; // Get the current value of the input field
                            handleCustomField(item.id, inputValue);
                            await trigger(item.code);
                          }}
                        />
                      )}
                      {item.type === "number" && (
                        <input
                          id={item.code}
                          name={item.code}
                          type="number"
                          className={`form-control ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          placeholder={item.placeholder}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                            minLength: {
                              value: 5,
                              message: t("min_length_of_5"),
                            },
                          })}
                          onChange={async (e) => {
                            const inputValue = e.target.value; // Get the current value of the input field
                            handleCustomField(item.id, inputValue);
                            await trigger(item.code);
                          }}
                        />
                      )}
                      {item.type === "text" && item.code === "gender" && (
                        <select
                          id={item.code}
                          name={item.code}
                          className={`form-control ${
                            errors[item.code] ? "error-field" : ""
                          }`}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                          })}
                          onChange={async (e) => {
                            const inputValue = e.target.value; // Get the current value of the input field
                            handleCustomField(item.id, inputValue);
                            await trigger(item.code);
                          }}
                        >
                          {item?.options?.map((option, optionIndex) => (
                            <option key={optionIndex} value={option.value}>
                              {option.title}
                            </option>
                          ))}
                        </select>
                      )}
                      {item.type === "text" && item.code === "country" && (
                        <select
                          id={item.code}
                          name={item.code}
                          className={`form-control ${
                            errors[item.name] ? "error-field" : ""
                          }`}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                          })}
                          onChange={async (e) => {
                            const inputValue = e.target.value; // Get the current value of the input field
                            handleCountry(e.target.value, item.options);
                            handleCustomField(item.id, inputValue);
                            await trigger(item.code);
                          }}
                        >
                          <option>{t("select_the_country")}</option>
                          {item?.options?.map((option, optionIndex) => (
                            <option key={optionIndex} value={option.id}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      )}
                      {item.type === "text" && item.code === "state" && (
                        <select
                          id={item.code}
                          name={item.name}
                          className={`form-control ${
                            errors[item.name] ? "error-field" : ""
                          }`}
                          {...register(item.code, {
                            required: {
                              value: item.required,
                              message: t("this_field_is_required"),
                            },
                          })}
                          onChange={async (e) => {
                            const inputValue = e.target.value; // Get the current value of the input field
                            handleCustomField(item.id, inputValue);
                            await trigger(item.code);
                          }}
                        >
                          {states?.map((option, optionIndex) => (
                            <option key={optionIndex} value={option.id}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      )}
                      {errors[item.code] && (
                        <span className="error-message-validator">
                          {errors[item.code].message}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <div className="col-md-12">
                  {/* <button
                    type="button"
                    className="btn btn-dark text-white float-start back mt-4 rounded-3 bg-color-back"
                    onClick={handleBack}
                  >
                    {t("back")}
                  </button> */}
                  <button
                    type="button"
                    className="btn text-white float-end next mt-4 rounded-3 bg-color-info"
                    onClick={handleNext}
                  >
                    {t("next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeStep === 3 && (
          <div className="row row_top justify-content-center form-business">
            <div className="regsiter_step_1_view_left_sec_head">
              {data?.registrationSteps &&
                (data.regPack === null && data.regFee === 0
                  ? t(data?.registrationSteps[activeStep - 2]?.label)
                  : data.regPack === null && data.regFee !== 0
                  ? t(data?.registrationSteps[activeStep - 1]?.label)
                  : t(data?.registrationSteps[activeStep - 1]?.label))}
            </div>
            <div className="regsiter_second_step_bx">
              <div className="row">
                {data?.loginInformation.map((item, index) => (
                  <div className="col-md-12" key={index}>
                    <div className="regsiter_form_box">
                      <label htmlFor={item.code}>
                        {t(item.code)} <span className="text-danger">٭</span>
                      </label>
                      {item.type === "text" && (
                        <>
                          <input
                            id={item.code}
                            name={item.code}
                            type={item.type}
                            className={`form-control ${
                              (!checkUsernameField.data?.data?.status &&
                                checkUsernameField.data?.data?.field ===
                                  "username" &&
                                checkUsernameField.data?.data?.code === 1117) ||
                              errors[item.code]
                                ? "error-field"
                                : ""
                            }`}
                            placeholder={item.placeholder}
                            value={item.value}
                            disabled={true}
                            {...register(item.code)}
                            // {...register(
                            //   item.code,
                            //   loginUsernameValidator(item, t)
                            // )}
                            // onChangeCapture={async (e) => {
                            //   await handleUsernameCheck(e.target);
                            // }}
                            // onBlur={async () => await trigger(item.code)}
                          />
                          {!checkUsernameField.data?.data?.status &&
                            checkUsernameField.data?.data?.field ===
                              "username" &&
                            checkUsernameField.data?.data?.code === 1117 && (
                              <span className="error-message-validator">
                                {t("username_exists")}
                              </span>
                            )}
                        </>
                      )}
                      {item.type === "password" && (
                        <>
                          <input
                            id={item.code}
                            name={item.code}
                            type={showPassword ? "text" : "password"}
                            style={{ marginBottom: "8px" }}
                            className={`form-control ${
                              errors[item.code] ? "error-field" : ""
                            }`}
                            placeholder={item.placeholder}
                            {...register(
                              item.code,
                              loginPasswordValidator(item, t)
                            )}
                            onBlur={async () => await trigger(item.code)}
                          />
                          <InputAdornment
                            position="end"
                            className="replica-eye"
                          >
                            <IconButton
                              onClick={handleShowPassword}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                          <PasswordChecklist
                            rules={passwordRules(item.validation)}
                            minLength={item.validation.minLength}
                            value={watch("password", "")}
                            className="mb-3"
                          />
                          <label htmlFor={"confirmPassword"}>
                            {t("confirmPassword")}{" "}
                            <span className="text-danger">٭</span>
                          </label>
                          <input
                            id={"confirmPassword"}
                            name={"confirmPassword"}
                            type={"password"}
                            style={{ marginBottom: "8px" }}
                            className={`form-control ${
                              errors["confirmPassword"] ? "error-field" : ""
                            }`}
                            placeholder={item.placeholder}
                            {...register("confirmPassword", {
                              required: {
                                message: t("this_field_is_required"),
                              },
                              validate: (value) =>
                                value === getValues("password") ||
                                t("passwordNotMatch"),
                            })}
                            onBlur={async () =>
                              await trigger("confirmPassword")
                            }
                          />
                        </>
                      )}
                      {errors[item.code] && (
                        <span className="error-message-validator">
                          {t(errors[item.code].message)}
                        </span>
                      )}
                      {errors["confirmPassword"] &&
                        item.code !== "username" && (
                          <span className="error-message-validator">
                            {errors["confirmPassword"].message}
                          </span>
                        )}
                    </div>
                  </div>
                ))}
                <div className="col-md-12">
                  <div className="regsiter_form_box">
                    <label className="d-flex" htmlFor="box">
                      <input
                        name={"termsAndCondition"}
                        type="checkbox"
                        id="box"
                        style={{ marginRight: "5px" }}
                        {...register("termsAndCondition", {
                          required: true,
                        })}
                      />
                      <a
                        data-bs-toggle="modal"
                        className="pop_terms_btn"
                        onClick={handleToggleRegisterModal}
                      >
                        {t("iAcceptTermsAndConditions")}
                        <span className="text-danger"> ٭ </span>
                      </a>
                    </label>
                    {errors["termsAndCondition"] &&
                      formValues["termsAndCondition"] === false && (
                        <span className="error-message-validator">
                          *{t("required")}
                        </span>
                      )}
                  </div>
                </div>
                <div className="col-md-12">
                  <button
                    type="button"
                    className="btn btn-dark text-white float-start back mt-4 rounded-3 bg-color-back"
                    onClick={handleBack}
                  >
                    {t("back")}
                  </button>
                  <button
                    type="button"
                    className="btn text-white float-end next mt-4 rounded-3 bg-color-info"
                    onClick={handleNext}
                  >
                    {t("next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* {activeStep === 4 && (
          <div className="row row_top justify-content-center form-business">
            <div className="regsiter_second_step_bx">
              <div className="regsiter_overview_box">

                <div className="regsiter_step_1_view_left_sec_head bg-transparent ps-0 pb-4">
                  <strong>Payment Details</strong>
                </div>

                <div className="row">
                  <div className="col-md-6" >
                    <div className="regsiter_form_box">
                      <label>Nominee Name</label>
                      <input
                        id={'nomineeName'}
                        name={'nomineeName'}
                        type="text"
                        className={`form-control ${errors['nomineeName'] ? "error-field" : ""
                          }`}
                        {...register('nomineeName')}
                      />
                    </div>
                  </div>
                  <div className="col-md-6" >
                    <div className="regsiter_form_box">
                      <label>Relation</label>
                      <input
                        id={'relation'}
                        name={'relation'}
                        type="text"
                        className={`form-control ${errors['relation'] ? "error-field" : ""
                          }`}
                        {...register('relation')}
                      />
                    </div>
                  </div>
                </div>

             
                <div className="row">
                  <div className="col-md-6" >
                    <div className="regsiter_form_box">
                      <label>PAN Number <span className="text-danger">*</span></label>
                      <input
                        id={'panNumber'}
                        name={'panNumber'}
                        type="text"
                        className={`form-control ${errors['panNumber'] ? "error-field" : ""
                          }`}
                        {...register('panNumber', {
                          required: {
                            value: 'panNumber',
                            message: t("this_field_is_required"),
                          }
                        })}
                      />
                      {errors.panNumber &&
                        <span className="error-message-validator">
                          {errors.panNumber.message}
                        </span>
                      }
                    </div>
                  </div>
                  <div className="col-md-6" >
                    <div className="regsiter_form_box">
                      <label>Aadhar Number</label>
                      <input
                        id={'aadharNumber'}
                        name={'aadharNumber'}
                        type="text"
                        className={`form-control ${errors['aadharNumber'] ? "error-field" : ""
                          }`}
                        {...register('aadharNumber')}
                      />
                    </div>
                  </div>
                </div>


                <div className="row">
                  <div className="col-md-6" >
                    <div className="regsiter_form_box">
                      <label>Bank Account Name</label>
                      <input
                        id={'bankAcName'}
                        name={'bankAcName'}
                        type="text"
                        className={`form-control ${errors['bankAcName'] ? "error-field" : ""
                          }`}
                        {...register('bankAcName')}
                      />
                    </div>
                  </div>
                  <div className="col-md-6" >
                    <div className="regsiter_form_box">
                      <label>Bank Account Number</label>
                      <input
                        id={'bankAcNumber'}
                        name={'bankAcNumber'}
                        type="text"
                        className={`form-control ${errors['bankAcNumber'] ? "error-field" : ""
                          }`}
                        {...register('bankAcNumber')}
                      />
                    </div>
                  </div>
                  <div className="col-md-6" >
                    <div className="regsiter_form_box">
                      <label>IFSC Code</label>
                      <input
                        id={'ifsc'}
                        name={'ifsc'}
                        type="text"
                        className={`form-control ${errors['ifsc'] ? "error-field" : ""
                          }`}
                        {...register('ifsc')}
                      />
                    </div>
                  </div>
                  <div className="col-md-6" >
                    <div className="regsiter_form_box">
                      <label>Bank Name</label>
                      <input
                        id={'bankName'}
                        name={'bankName'}
                        type="text"
                        className={`form-control ${errors['bankName'] ? "error-field" : ""
                          }`}
                        {...register('bankName')}
                      />
                    </div>
                  </div>
                  <div className="col-md-6" >
                    <div className="regsiter_form_box">
                      <label>Branch Name</label>
                      <input
                        id={'branchName'}
                        name={'branchName'}
                        type="text"
                        className={`form-control ${errors['branchName'] ? "error-field" : ""
                          }`}
                        {...register('branchName')}
                      />
                    </div>
                  </div>


                </div>

                <div className="col-md-12">
                  <button
                    type="button"
                    className="btn btn-dark text-white float-start back mt-4 rounded-3 bg-color-back"
                    onClick={handleBack}
                  >
                    {t("back")}
                  </button>
                  <button
                    type="button"
                    className="btn text-white float-end next mt-4 rounded-3 bg-color-info"
                    onClick={handleNext}
                  >
                    {t("next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )} */}
        {activeStep === 4 && (
          <div className="row row_top justify-content-center form-business">
            <div className="regsiter_second_step_bx">
              <div className="regsiter_overview_box">
                <div className="regsiter_step_1_view_left_sec_head">
                  <strong>{t("product_and_sponsor")}</strong>
                </div>
                <div className="row">
                  {data?.modStatus?.productStatus !== 0 && (
                    <div className="col-md-4 mb-3 regsiter_overview_col">
                      <label htmlFor="product">{t("product")}</label>
                      <strong id="product">{formValues?.product?.name}</strong>
                    </div>
                  )}
                  <div className="col-md-4 mb-3 regsiter_overview_col">
                    <label htmlFor="sponsor">{t("sponsor")}</label>
                    <strong id="sponsor">{selectedSponsor?.label ?? data?.sponsorData?.username}</strong>
                  </div>
                  {data?.regFee !== 0 && (
                    <div className="col-md-4 mb-3 regsiter_overview_col">
                      <label htmlFor="pv">{t("pv")}</label>
                      <strong id="pv">{formValues?.pv ?? "0"}</strong>
                    </div>
                  )}
                  <div className="col-md-4 mb-3 regsiter_overview_col">
                    <label htmlFor="position">{t("position")}</label>
                    <strong id="position">
                      {data?.position ?? formValues.position}
                    </strong>
                  </div>
                  {/* <div className="col-md-4 mb-3 regsiter_overview_col">
                    <label htmlFor="price">{t("price")}</label>
                    <strong id="price">{`${userSelectedCurrency?.symbolLeft
                      } ${CurrencyConverter(
                        formValues?.product?.price ?? data?.regData,
                        conversionFactor
                      )}`}</strong>
                  </div>
                  <div className="col-md-4 mb-3 regsiter_overview_col">
                    <label htmlFor="total">{t("totalAmount")}</label>
                    <strong id="total">{`${userSelectedCurrency?.symbolLeft
                      } ${CurrencyConverter(
                        formValues?.totalAmount ?? data?.regData,
                        conversionFactor
                      )}`}</strong>
                  </div> */}
                </div>

                <div className="regsiter_step_1_view_left_sec_head">
                  <strong>{t(`${data?.registrationSteps[1]?.label}`)}</strong>
                </div>

                <div className="row">
                  {Object.entries(formValues)?.map(([key, value]) => {
                    if (
                      ![
                        "product",
                        "PV",
                        "regAmount",
                        "termsAndCondition",
                        "totalAmt",
                        "paymentType",
                        "customFields",
                        "bankReceipt",
                        "referralId",
                        "totalAmount",
                        "epins",
                        "regAmount",
                        "transactionPassword",
                        "totalEpinAmt",
                        "username",
                        "password",
                        "pv",
                        "confirmPassword",
                        "noProduct",
                        "position",
                      ].includes(key)
                    ) {
                      if (
                        (key === "country" ||
                          key === "state" ||
                          key === "city") &&
                        value !== undefined &&
                        value !== null
                      ) {
                        return (
                          <div
                            className="col-md-4 mb-3 regsiter_overview_col"
                            key={key}
                          >
                            <label htmlFor={`input-${key}`}>{t(key)}</label>
                            <strong id={`input-${key}`}>
                              {key === "country" &&
                                data.contactInformation.contactField
                                  .find((_) => _.code === "country")
                                  ?.options.find(
                                    (country) => country.id === value
                                  )?.name}
                              {key === "state" &&
                                data.contactInformation.contactField
                                  .find((_) => _.code === "country")
                                  ?.options.find(
                                    (country) =>
                                      country.id === formValues.country
                                  )
                                  ?.States.find((state) => state.id === value)
                                  ?.name}
                              {key === "city" &&
                                data.districtsData.find(
                                  (district) => district.value === value
                                )?.label}
                            </strong>
                          </div>
                        );
                      } else if (
                        key !== "country" &&
                        key !== "state" &&
                        key !== "city"
                      ) {
                        return (
                          <div
                            className="col-md-4 mb-3 regsiter_overview_col"
                            key={key}
                          >
                            <label htmlFor={`input-${key}`}>{t(key)}</label>
                            <strong id={`input-${key}`}>{value}</strong>
                          </div>
                        );
                      }
                    }
                    return null; // Return null for keys in the exclusion list or when country/state value is null/undefined
                  })}
                  {formValues?.customField?.map(([key, value]) => (
                    <div className="col-md-4 mb-3 regsiter_overview_col">
                      <label htmlFor={`input-${key}`}>{t(key)}</label>
                      <strong id={`input-${key}`}>{value}</strong>
                    </div>
                  ))}
                </div>

                <div className="regsiter_step_1_view_left_sec_head">
                  <strong>{t(`${data?.registrationSteps[2]?.label}`)}</strong>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3 regsiter_overview_col">
                    <label htmlFor="username">{t("username")}</label>
                    <strong id="username">{formValues.username}</strong>
                  </div>
                  <div className="col-md-4 mb-3 regsiter_overview_col">
                    <label htmlFor="password">{t("password")}</label>
                    <strong id="password">{formValues.password}</strong>
                  </div>
                </div>

                <div className="col-md-12">
                  <button
                    type="button"
                    className="btn btn-dark text-white float-start back mt-4 rounded-3 bg-color-back"
                    onClick={handleBack}
                  >
                    {t("back")}
                  </button>
                  {data.regData === 0 && !data.regPack ? (
                    <button
                      type="submit"
                      className="btn text-white float-end submit-button rounded-3 bg-color-info"
                      disabled={submitButtonActive}
                      onClick={handleSubmit}
                    >
                      {t("finish")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn text-white float-end next mt-4 rounded-3 bg-color-info"
                      onClick={handleNext}
                    >
                      {t("next")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {activeStep === 5 && (
          <div className="row row_top justify-content-center form-business">
            <div className="col-lg-12 col-md-12" id="payment">
              <div className="payment_section_tab">
                <div className="regsiter_step_1_view_left_sec_head">
                  {data?.registrationSteps &&
                    (data.regPack === null && data.regFee === 0
                      ? t(data?.registrationSteps[activeStep - 2]?.label)
                      : data.regPack === null && data.regFee !== 0
                      ? t(data?.registrationSteps[activeStep - 1]?.label)
                      : t(data?.registrationSteps[activeStep - 1]?.label))}
                  <br />
                  <strong>
                    {t("totalAmount")}:{" "}
                    {`${userSelectedCurrency?.symbolLeft} ${CurrencyConverter(
                      formValues?.totalAmount ?? data?.regFee,
                      conversionFactor
                    )}`}
                  </strong>
                </div>

                <div className="row">
                  <div className="col-md-3 pe-0">
                    <div
                      className="nav nav-tabs payment_tab_reg"
                      id="nav-tab"
                      role="tablist"
                    >
                      {data?.paymentGateways?.map((tab) => (
                        <Fragment key={tab.id}>
                          <button
                            key={tab.id}
                            className={`nav-link ${
                              activeTab === tab.id ? "active" : ""
                            }`}
                            id={`nav-${tab.id}-tab`}
                            data-bs-toggle="tab"
                            data-bs-target={`#nav-${tab.id}`}
                            type="button"
                            role="tab"
                            aria-controls={`nav-${tab.id}`}
                            aria-selected={formValues.paymentType === tab.id}
                            {...register("paymentType", { required: true })}
                            onClick={() =>
                              handlePaymentTabClick(tab.id, tab.title)
                            }
                          >
                            <i className={tab?.icon}></i> {t(tab.title)}
                          </button>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                  <div className="col-md-9 ps-0">
                    <div
                      className="tab-content p-3 border mb-5 payment__tab_cnt"
                      id="nav-tabContent"
                    >
                      {(formValues.paymentType === undefined ||
                        formValues.paymentType === "") && (
                        <p>
                          <strong>{t("preferredPaymentPlaceholder")}</strong>
                        </p>
                      )}

                      {data?.paymentGateways?.map((tab) => (
                        <div
                          key={tab.id}
                          className={`tab-pane fade ${
                            activeTab === tab.id ? "show active" : ""
                          }`}
                          id={`nav-${tab.id}`}
                          role="tabpanel"
                          aria-labelledby={`nav-${tab.id}-tab`}
                        >
                          {tab.title === "free-joining" && (
                            <p>
                              <strong>
                                {t("freeJoinPlaceHolder", {
                                  type: "registration",
                                })}
                              </strong>
                            </p>
                          )}
                          {tab.title === "bank-transfer" && (
                            <div className="row">
                              <div className="col-md-12">
                                <div className="form-group mb-2">
                                  <label
                                    htmlFor="fileUpload"
                                    style={{ marginBottom: "10px" }}
                                  >
                                    {t("file_upload")}
                                  </label>
                                  <input
                                    id="bankReciept"
                                    type="file"
                                    placeholder=""
                                    className="form-control"
                                    style={{ height: "33px" }}
                                    name="fileUpload"
                                    onChange={handleFileChange}
                                  />
                                  {fileResponse?.success &&
                                    formValues?.bankReceipt !== undefined && (
                                      <div style={{ color: "green" }}>
                                        {t(fileResponse?.success)}
                                      </div>
                                    )}
                                  {fileResponse?.error && (
                                    <div style={{ color: "red" }}>
                                      {t(fileResponse?.error)}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-12">
                                <button
                                  href="#"
                                  className="btn btn-sm btn-primary mt-3"
                                  onClick={handleUpload}
                                  disabled={
                                    Upload.status === "loading" ||
                                    formValues?.bankReceipt ||
                                    document.getElementById("bankReciept")
                                      ?.value === "" ||
                                    file === null
                                  }
                                >
                                  {Upload.status === "loading"
                                    ? "Uploading..."
                                    : t("upload")}
                                </button>
                              </div>
                              {Upload?.data?.status === true &&
                                formValues?.bankReceipt !== undefined && (
                                  <>
                                    <img
                                      src={`${BASE_URL}${Upload?.data?.data?.file?.path}`}
                                      alt="receipt"
                                      style={{
                                        width: "160px",
                                        height: "160px",
                                        padding: "5px",
                                      }}
                                    />
                                    <button
                                      className="recipt_checkout_address_btn"
                                      onClick={handleDeleteBankReciept}
                                    >
                                      <i className="fa fa-xmark"></i>
                                    </button>
                                    <div style={{ color: "green" }}>
                                      {Upload?.data?.message}
                                    </div>
                                  </>
                                )}
                              {Upload?.data?.status === false && (
                                <span className="error-message-validator">
                                  {Upload?.data?.message}
                                </span>
                              )}
                            </div>
                          )}
                          {tab.title === "stripe" && clientSecret !== "" && (
                            <p>
                              <Stripe
                                clientSecret={clientSecret}
                                handleSubmit={handleSubmit}
                                publicKey={stripePublicKey}
                              />
                            </p>
                          )}
                          {tab.title === "paypal" &&
                            formValues.totalAmount > 0 && (
                              <MyPayPalOrderButton
                                // currency={userSelectedCurrency?.code}
                                currency={tab?.currency}
                                price={formValues.totalAmount}
                                handleSubmit={handleSubmit}
                                paymentId={tab.id}
                                paymentType={tab.title}
                              />
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {errors.paymentType && (
                  <span className="error-message-validator">*Required</span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-dark text-white float-start back rounded-3 bg-color-back"
                onClick={handleBack}
              >
                {t("back")}
              </button>
              {activeTab !== 5 && (
                <button
                  type="submit"
                  className="btn text-white float-end submit-button rounded-3 bg-color-info"
                  disabled={submitButtonActive}
                  onClick={handleSubmit}
                >
                  {t("finish")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      {replicaRegisterMutation.status === "loading" && (
        <div className="reg-submit-loader-overlay">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xlink="http://www.w3.org/1999/xlink"
            style={{ margin: "auto", display: "block", shapeRendering: "auto" }}
            width="200px"
            height="200px"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid"
          >
            <path
              fill="none"
              stroke="#7b33c2"
              stroke-width="8"
              stroke-dasharray="42.76482137044271 42.76482137044271"
              d="M24.3 30C11.4 30 5 43.3 5 50s6.4 20 19.3 20c19.3 0 32.1-40 51.4-40 C88.6 30 95 43.3 95 50s-6.4 20-19.3 20C56.4 70 43.6 30 24.3 30z"
              stroke-linecap="round"
              style={{ transform: "scale(0.8)", transformOrigin: "50px 50px" }}
            >
              <animate
                attributeName="stroke-dashoffset"
                repeatCount="indefinite"
                dur="1s"
                keyTimes="0;1"
                values="0;256.58892822265625"
              ></animate>
            </path>
          </svg>
        </div>
      )}
    </div>
  );
};

export default ReplicaRegisterForm;
